import { bindable } from 'aurelia-typed-observable-plugin';
import { getKey, Key } from 'app/shared/lib/keys';
import { I18N } from 'aurelia-i18n';
import { microTask } from 'app/shared/lib/decorators';
import { autoinject, bindingMode, TaskQueue } from 'aurelia-framework';
import { deepClone } from 'app/shared/lib/utilities';
import { guid } from 'app/shared/lib/guid';


export interface IListItem {
    order: number, // represents original order position
    value: string,
    id: string, // unique identifier for the list item
    isActive: boolean
}

@autoinject()
export class EssSortableListCustomElement<T extends IListItem> {

    @bindable.array({ defaultBindingMode: bindingMode.twoWay })
    listItems: Array<T> = [];

    ////****************** Element References ***************************************////
    protected $list!: HTMLElement;
    protected $liveMessage!: HTMLSpanElement;

    protected id: guid = guid();

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(protected readonly i18n: I18N,
        protected readonly taskQueue: TaskQueue,
    ) {
    }

    ////****************** Events ***************************************************////

    protected handleKeyDownMoveUp(event: KeyboardEvent, listItem: T, moveToTop: boolean = false) {
        switch(getKey(event)) {
            case Key.Enter:
            case Key.Space:
                this.handleMoveUp(event, listItem, moveToTop);
                return true;
        }
        return true;
    }

    protected handleKeyDownMoveDown(event: KeyboardEvent, listItem: T, moveToBottom: boolean = false) {
        switch(getKey(event)) {
            case Key.Enter:
            case Key.Space:
                this.handleMoveDown(event, listItem, moveToBottom);
                return true;
        }
        return true;
    }

    protected async handleMoveUp(event: MouseEvent | KeyboardEvent, listItem: T, moveToTop: boolean = false): Promise<true | void> {
        const listItems = Array.from(this.$list.children as Iterable<HTMLElement>);
        const listItemElement = listItems.find((listItem) => listItem.contains(event.target as HTMLElement));
        const listItemId = listItemElement?.id as string;
        if(!listItemElement) { return true; }

        const currentPosition = this.listItems.indexOf(listItem);
        const listItemCount = this.$list.children.length;
        let newPosition = Math.max(currentPosition - 1, 0);

        if(moveToTop) {
            newPosition = 0;
        }

        await this.moveListItems(currentPosition, newPosition, moveToTop, false);

        const ariaMessage = this.i18n.tr('ui:sortableList.reorder.position', { index: newPosition + 1, length: listItemCount });
        this.updateLiveText(ariaMessage);
        const newListItemElement = document.getElementById(listItemId);

        if(moveToTop) {
            // scroll to top of list
            this.$list.firstElementChild?.scrollIntoView({ block: "start", behavior: "auto" });
            const arrowTop = newListItemElement?.querySelector("i[ess-icon='ui:icons.arrow-up-to-line']") as HTMLElement;
            arrowTop?.focus();
        } else {
            // scroll to the new position of the list item
            if(currentPosition >= 1) {
                this.$list.children[currentPosition - 1]?.scrollIntoView({ block: "center", behavior: "auto" });
            }
            const arrowUp = newListItemElement?.querySelector("i[ess-icon='ui:icons.arrow-up']") as HTMLElement;
            arrowUp?.focus();
        }
    }

    protected async handleMoveDown(event: MouseEvent | KeyboardEvent, listItem: T, moveToBottom: boolean = false): Promise<true | void> {
        const listItems = Array.from(this.$list.children as Iterable<HTMLElement>);
        const listItemElement = listItems.find((listItem) => listItem.contains(event.target as HTMLElement));
        const listItemId = listItemElement?.id as string;
        if(!listItemElement) { return true; }

        const currentPosition = this.listItems.indexOf(listItem);
        const listItemCount = this.$list.children.length;
        let newPosition = Math.max(currentPosition + 1, 0);
        if(newPosition >= listItemCount) {
            newPosition = listItemCount - 1;
        }

        if(moveToBottom) {
            newPosition = listItemCount - 1;
        }
        await this.moveListItems(currentPosition, newPosition, false, moveToBottom);

        const ariaMessage = this.i18n.tr('ui:sortableList.reorder.position', { index: newPosition + 1, length: listItemCount });
        this.updateLiveText(ariaMessage);
        const newListItemElement = document.getElementById(listItemId);

        if(moveToBottom) {
            // scroll to bottom of list
            this.$list.lastElementChild?.scrollIntoView({ block: "start", behavior: "auto" });
            const arrowBottom = newListItemElement?.querySelector("i[ess-icon='ui:icons.arrow-down-to-line']") as HTMLElement;
            arrowBottom?.focus();

        } else {
            // scroll to the new position of the list item
            if(currentPosition != newPosition) {
                this.$list.children[currentPosition + 1].scrollIntoView({ block: "center", behavior: "auto" });
            }
            const arrowDown = newListItemElement?.querySelector("i[ess-icon='ui:icons.arrow-down']") as HTMLElement;
            arrowDown?.focus();
        }
    }

    ////****************** Functions ************************************************////

    protected updateLiveText(announcement: string) {
        // code to update text to our aria-live span
        this.$liveMessage.textContent = announcement;
    }

    public reset() {
        this.resetOrder();
    }

    @microTask
    protected resetOrder() {
        this.listItems = this.listItems.sort((a: T, b: T) => a.order - b.order);
    }

    protected moveListItems(Item1Index: number, item2Index: number, moveToTop: boolean = false, moveToBottom: boolean = false) {
        // insure indices are within range
        if(Item1Index < 0 || item2Index < 0 || Item1Index >= this.listItems.length || item2Index >= this.listItems.length) return;
        const listItemsCopy = deepClone(this.listItems);

        if(moveToTop || moveToBottom) {
            if(moveToBottom) {
                listItemsCopy.push(listItemsCopy.splice(Item1Index, 1)[0]);
            } else if(moveToTop) {
                listItemsCopy.unshift(listItemsCopy.splice(Item1Index, 1)[0]);
            }
        } else {
            const item1 = listItemsCopy[Item1Index];
            listItemsCopy[Item1Index] = listItemsCopy[item2Index];
            listItemsCopy[item2Index] = item1;
        }
        this.listItems = listItemsCopy;
    }

}
